import SessionManager from '../utils/auth/SessionManager';

// API Gateway configuration - loaded from runtime config.js
const API_CONFIG = {
  baseUrl: window.APP_CONFIG.API_BASE_URL
};

/**
 * Get Cognito ID token from SessionManager
 */
const getIdToken = () => {
  return SessionManager.getIdToken();
};

/**
 * Fetch funding requests from API Gateway
 * GET /validations — returns all FRs (admin view)
 * GET /validations?user=alias — returns only FRs for that user
 * GET /validations?days=30 — returns FRs from the last 30 days
 */
export const fetchFundingRequests = async (user = null, days = null) => {
  console.log('Fetching funding requests from API...');
  const idToken = getIdToken();

  if (!idToken) {
    console.error('No authentication token found');
    throw new Error('No authentication token found. Please log in.');
  }

  const params = new URLSearchParams();
  if (user) params.set('user', user);
  if (days) params.set('days', days);
  const queryString = params.toString();
  const url = queryString
    ? `${API_CONFIG.baseUrl}/validations?${queryString}`
    : `${API_CONFIG.baseUrl}/validations`;
  console.log('Making request to:', url);

  const response = await fetch(url, {
    method: 'GET',
    cache: 'no-store',
    headers: {
      'Authorization': idToken,
      'Content-Type': 'application/json'
    }
  });

  console.log('Response status:', response.status);
  console.log('Response headers:', Object.fromEntries(response.headers.entries()));

  if (response.status === 401) {
    console.error('Authentication failed');
    throw new Error('Authentication failed. Please log in again.');
  }

  if (response.status === 403) {
    console.error('Access denied');
    throw new Error('Access denied. You do not have permission to view funding requests.');
  }

  if (!response.ok) {
    const errorText = await response.text();
    console.error('API request failed:', response.status, errorText);
    throw new Error(`Failed to fetch funding requests: ${response.statusText} - ${errorText}`);
  }

  const data = await response.json();
  console.log('Raw API response:', data);

  // Transform DynamoDB data to match UI format if needed
  const transformed = transformFundingRequests(data);
  console.log('Transformed data:', transformed);
  
  return transformed;
};

/**
 * Get review status for all funding requests or filtered by status
 * GET /review-status
 * GET /review-status?status={status}
 */
export const getAllReviewStatus = async (statusFilter = null) => {
  console.log('Fetching review status with filter:', statusFilter);
  const idToken = getIdToken();

  if (!idToken) {
    console.error('No authentication token found');
    throw new Error('No authentication token found. Please log in.');
  }

  const endpoint = statusFilter 
    ? `${API_CONFIG.baseUrl}/review-status?status=${statusFilter}`
    : `${API_CONFIG.baseUrl}/review-status`;

  console.log('Making request to:', endpoint);

  const response = await fetch(endpoint, {
    method: 'GET',
    cache: 'no-store',
    headers: {
      'Authorization': idToken,
      'Content-Type': 'application/json'
    }
  });

  console.log('Review status response status:', response.status);

  if (response.status === 401) {
    console.error('Authentication failed');
    throw new Error('Authentication failed. Please log in again.');
  }

  if (response.status === 403) {
    console.error('Access denied');
    throw new Error('Access denied. You do not have permission to view review status.');
  }

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Review status API request failed:', response.status, errorText);
    throw new Error(`Failed to fetch review status: ${response.statusText} - ${errorText}`);
  }

  const data = await response.json();
  console.log('Raw review status response:', data);
  
  const transformed = transformFundingRequests(data);
  console.log('Transformed review status data:', transformed);
  
  return transformed;
};

/**
 * Transform nested DynamoDB validation_data to SOP-organized structure for React app
 */
const transformValidationData = (item) => {
  console.log('Transforming validation data item:', item);
  
  // If already transformed (has 'id' field), return as-is
  if (item.id || item.requestor) {
    console.log('Item already transformed, returning as-is');
    return item;
  }

  // For actual DynamoDB structure, extract data from the correct locations
  if (item.validation_data?.complete_api_data?.fundRequest) {
    console.log('Processing actual DynamoDB structure with fundRequest');
    const fundRequest = item.validation_data.complete_api_data.fundRequest;
    const project = fundRequest.project || {};
    const partner = fundRequest.partner || {};
    const techData = item.validation_data.detailed_validations?.technical_requirements || {};
    const oppData = item.validation_data.detailed_validations?.opportunity_stage || {};
    
    // Extract customer and partner names from the actual data
    // First try to get customer from opportunities data
    const opportunities = fundRequest.opportunities || {};
    const firstOppKey = Object.keys(opportunities)[0];
    const firstOpp = firstOppKey ? (opportunities[firstOppKey].record || {}) : {};
    
    const customerName = firstOpp.customerCompanyName || 
                        item.approval_summary?.customer || 
                        oppData.customer_company_name || 
                        'Unknown Customer';
    const partnerName = item.approval_summary?.partner || partner.name || 'Unknown Partner';
    const migrationPhase = fundRequest.migrationPhase || techData.migration_phase || 'Unknown Phase';
    
    // Extract fund amount from approval_summary or fundRequest
    let fundAmount = 0;
    let arr = 0;
    
    // First try to get the actual approved cash amount
    if (item.approved_cash_amount) {
      fundAmount = parseFloat(item.approved_cash_amount);
    } else if (fundRequest.fundingDetails?.approvedCashLocalAmount) {
      fundAmount = parseFloat(fundRequest.fundingDetails.approvedCashLocalAmount.replace(/[^0-9.-]+/g, ''));
    } else if (item.approval_summary?.fund_amount) {
      fundAmount = parseFloat(item.approval_summary.fund_amount.replace(/[$,]/g, ''));
    } else if (fundRequest.eligibleArrUsdAmount) {
      fundAmount = parseFloat(fundRequest.eligibleArrUsdAmount.split(' ')[0]);
    }
    
    // Extract ARR - prioritize request_data_summary fields
    const requestSummary = item.validation_data?.request_data_summary || {};
    if (requestSummary.annual_recurring_revenue) {
      arr = requestSummary.annual_recurring_revenue;
    } else if (requestSummary.eligible_arr) {
      arr = requestSummary.eligible_arr;
    } else if (fundRequest.arrUsdAmount) {
      arr = parseFloat(fundRequest.arrUsdAmount.replace(/[^0-9.-]+/g, ''));
    } else if (fundRequest.eligibleArrUsdAmount) {
      arr = parseFloat(fundRequest.eligibleArrUsdAmount.replace(/[^0-9.-]+/g, ''));
    }
    
    const transformed = {
      ...item,
      id: item.apfp_fundrequestid,
      projectName: `${customerName} Migration`,
      customer: customerName,
      requestor: partnerName,
      amount: fundAmount,
      arr: arr,
      migrationPhase: migrationPhase,
      status: item.approval_status || 'pending'
    };
    
    console.log('Transformed actual DynamoDB structure item:', transformed);
    return transformed;
  }

  // Extract from validation_data structure
  const validationData = item.validation_data || {};
  const completeApi = validationData.complete_api_data || {};
  const fundRequest = completeApi.fundRequest || {};
  const detailed = validationData.detailed_validations || {};
  const finalDecision = detailed.final_decision || {};
  
  // NEW: Handle new data structure with opportunityDetails, mpeProgram, applications
  const opportunityDetails = completeApi.opportunityDetails || {};
  const mpeProgram = completeApi.mpeProgram || {};
  const applications = completeApi.applications || [];

  // Extract specific sections
  const partnerElig = detailed.partner_eligibility || {};
  const mpeValidation = detailed.mpe_program || {};
  const oppStage = detailed.opportunity_stage || {};
  const pricingCalc = detailed.pricing_calculator || {};
  const techReq = detailed.technical_requirements || {};
  const projectTimeline = detailed.project_timeline || {};

  // Get financial and validation results
  const financial = finalDecision.financial_summary || {};
  const validationResults = finalDecision.validation_results || {};

  // Extract partner and customer info
  const partnerInfo = fundRequest.partner || {};
  const ownerInfo = fundRequest.owner || {};
  const projectInfo = fundRequest.project || {};
  const opportunities = fundRequest.opportunities || {};

  // Get first opportunity
  const firstOppKey = Object.keys(opportunities)[0];
  const firstOpp = firstOppKey ? (opportunities[firstOppKey].record || {}) : {};

  const customerName = firstOpp.customerCompanyName || oppStage.customer_company_name || opportunityDetails.customerCompanyName || 'Unknown';
  const partnerName = partnerInfo.name || ownerInfo.partnerName || partnerElig.partner_name || 'Unknown';

  // Map decision to status
  const decision = finalDecision.decision || 'pending';
  const statusMap = {
    'recommend_approval': 'RECOMMEND_APPROVAL',
    'recommend_rejection': 'RECOMMEND_REJECTION',
    'needs_review': 'NEEDS_REVIEW',
    'RECOMMEND_APPROVAL': 'RECOMMEND_APPROVAL',
    'RECOMMEND_REJECTION': 'RECOMMEND_REJECTION',
    'NEEDS_REVIEW': 'NEEDS_REVIEW',
    // Backward compatibility for existing data
    'approved': 'RECOMMEND_APPROVAL',
    'rejected': 'RECOMMEND_REJECTION',
    'conditional_approval': 'NEEDS_REVIEW',
    'APPROVED': 'RECOMMEND_APPROVAL',
    'REJECTED': 'RECOMMEND_REJECTION'
  };
  const status = statusMap[decision] || 'pending';

  // Helper function to determine validation status
  const getValidationStatus = (result) => {
    if (!result) return 'unknown';
    if (result === 'validated' || result === 'eligible') return 'pass';
    if (result.includes('critical') || result === 'not_enrolled') return 'fail';
    return 'warning';
  };

  // Extract approval summary
  const approvalSummary = item.approval_summary || finalDecision.approver_summary || {};

  // Helper function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Extract actual fund amount (same logic as DynamoDB path)
  let extractedFundAmount = 0;
  if (item.approved_cash_amount) {
    extractedFundAmount = parseFloat(item.approved_cash_amount);
  } else if (fundRequest.fundingDetails?.approvedCashLocalAmount) {
    extractedFundAmount = parseFloat(fundRequest.fundingDetails.approvedCashLocalAmount.replace(/[^0-9.-]+/g, ''));
  }
  // Note: Removed old fund_amount check - backend now uses total_fund_requested

  // Build SOP-organized structure
  return {
    // Header data
    id: item.apfp_fundrequestid || fundRequest.fundRequestId || '',
    status: status,
    complianceScore: finalDecision.validation_summary?.display || `${finalDecision.overall_compliance_score ? Math.round(finalDecision.overall_compliance_score * 100) + '%' : '0/0 passed'}`,
    decision: decision,
    reason: finalDecision.reason || '',
    criticalFailures: finalDecision.critical_failures || [],
    
    // Preserve validation_data for component access
    validation_data: item.validation_data,
    approval_summary: item.approval_summary,
    processed_date: item.processed_date,

    // Validation Summary Tab (Overview)
    validationSummary: {
      decision: approvalSummary.decision || decision,
      recommendation: approvalSummary.recommendation || finalDecision.reason || '',
      complianceScore: approvalSummary.validation_summary || finalDecision.validation_summary?.display || `${Math.round((finalDecision.overall_compliance_score || 0) * 100)}%`,
      fundAmount: (() => {
        console.log('[FUND AMOUNT DEBUG]');
        console.log('extractedFundAmount:', extractedFundAmount);
        console.log('approvalSummary.total_fund_requested:', approvalSummary.total_fund_requested);
        console.log('fundRequest.fundingDetails?.approvedCashLocalAmount:', fundRequest.fundingDetails?.approvedCashLocalAmount);
        
        // Try extracted amount first
        if (extractedFundAmount > 0) {
          const result = formatCurrency(extractedFundAmount);
          console.log('Using extractedFundAmount, result:', result);
          return result;
        }
        // Try total_fund_requested from approval summary
        if (approvalSummary.total_fund_requested) {
          console.log('Using total_fund_requested:', approvalSummary.total_fund_requested);
          return approvalSummary.total_fund_requested;  // Already formatted
        }
        console.log('Returning dash');
        return '-';
      })(),
      keyFindings: approvalSummary.key_findings || [],
      requiredActions: approvalSummary.required_actions || [],
      criticalFailures: finalDecision.critical_failures || [],
      validationResults: validationResults,
      processedDate: item.processed_date || '-'
    },

    // Partner Eligibility Tab (SOP Sections 1.0-1.4)
    partnerEligibility: {
      partnerInformation: {
        partnerName: partnerName,
        spmsId: partnerElig.spms_id || partnerInfo.spmsId || ownerInfo.partnerSpmsId || '-',
        partnerVendorId: partnerInfo.vendorId || '-',
        eligibilityStatus: partnerElig.status || 'unknown',
        eligibilityFactors: partnerElig.eligibility_factors || []
      },
      competencyDetails: {
        mcpStatus: partnerElig.competency_details?.has_mcp ? 'Active' : 'Inactive',
        mcpCompetencyType: partnerElig.competency_details?.mcp_details?.competency_type || '-',
        mcrStatus: partnerElig.competency_details?.has_mcr ? 'Active' : 'Inactive',
        mcrExpirationDate: partnerElig.competency_details?.mcr_details?.expiration_date || '-',
        majorPartnerStatus: partnerElig.checkpoint?.details?.is_major_partner || false,
        gsiEligible: partnerElig.checkpoint?.details?.gsi_eligible || false,
        gsiAccount: partnerInfo.gsiAccount || '-'
      },
      validationStatus: getValidationStatus(validationResults.partner_eligibility),
      validationReason: partnerElig.reason || '',
      sopSections: partnerElig.sop_sections || []
    },

    // MPE Program Tab (SOP Sections 2.0-2.4)
    mpeProgram: {
      programStatus: {
        mpeProgram: mpeValidation.status === 'enrolled' ? 'enrolled' : 'not_enrolled',
        programEngagementId: mpeValidation.program_engagement_id || mpeProgram.programEngagementId || '-',
        mpeRecordStatus: mpeValidation.mpe_record_status || mpeProgram.recordStatus || '-',
        mpeEngagementStatus: mpeValidation.mpe_engagement_status || mpeProgram.engagementStatus || '-',
        estimatedMigrationARR: mpeValidation.estimated_migration_arr || mpeProgram.estimatedMigrationArr || 0,
        arrVariancePercent: mpeValidation.arr_variance_percent || 0
      },
      supportingDocuments: {
        detailedDiscovery: techReq.validation_checks?.workstream_compliance?.['Detailed Discovery'] || false,
        planningGovernance: techReq.validation_checks?.workstream_compliance?.['Planning & Governance'] || false,
        migrationModernizationExperience: techReq.validation_checks?.workstream_compliance?.['Migration & Modernization Experience'] || false
      },
      validationStatus: getValidationStatus(validationResults.mpe_program),
      validationReason: mpeValidation.reason || '',
      sopSections: mpeValidation.sop_sections || []
    },

    // Opportunity Stage Tab (SOP Sections 3.0-3.2)
    opportunityStage: {
      opportunityDetails: {
        opportunityId: oppStage.opportunity_id || firstOpp.aceOpportunityId || '-',
        awsOpportunityId: oppStage.aws_opportunity_id || firstOpp.awsOpportunityId || '-',
        customerCompanyName: customerName,
        opportunityStage: firstOpp.opportunityStage || '-',
        aceValidationStage: oppStage.ace_validation_stage || firstOpp.aceValidationStage || '-'
      },
      validationStatus: getValidationStatus(validationResults.opportunity_stage),
      validationReason: oppStage.reason || '',
      sopSections: oppStage.sop_sections || []
    },

    // Pricing Calculator Tab (SOP Sections 5.0-5.3)
    pricingCalculator: {
      calculatorDetails: {
        pricingCalculator: pricingCalc.calculator_url ? 'run' : 'not_run',
        calculatorURL: pricingCalc.calculator_url || '-',
        calculatorARR: pricingCalc.calculator_total_arr || financial.calculator_total_arr || 0,
        fundAmountRequested: financial.fund_amount_requested || 0,
        excludedServices: pricingCalc.excluded_services_found || [],
        notOptimizedPercentage: pricingCalc.not_optimized_percentage || financial.not_optimized_percentage || 0,
        mapProgram: pricingCalc.map_program || '-',
        qualificationARR: pricingCalc.qualification_arr || 0,
        excludedARR: pricingCalc.excluded_arr || 0
      },
      validationStatus: getValidationStatus(validationResults.pricing_calculator),
      validationReason: pricingCalc.reason || '',
      sopSections: pricingCalc.sop_sections || []
    },

    // Technical Requirements Tab (SOP Sections 6.0-6.5)
    technicalRequirements: {
      migrationDetails: {
        migrationPhase: techReq.migration_phase || fundRequest.migrationPhase || '-',
        technicalRequirements: techReq.status || '-',
        scopeChecklistPresent: techReq.scope_checklist_present || false,
        criticalFailures: techReq.critical_failures || [],
        workstreamCompliance: techReq.validation_checks?.workstream_compliance || {},
        translationNote: detailed.attached_files?.validations?.scope_checklist?.translation_note || null
      },
      validationStatus: getValidationStatus(validationResults.technical_requirements),
      validationReason: techReq.reason || '',
      sopSections: techReq.sop_sections || []
    },

    // Project Timeline Tab (SOP Sections 7.0-7.2)
    projectTimeline: {
      timelineDetails: {
        projectStartDate: projectTimeline.project_start_date || projectInfo.startDate || '-',
        projectEndDate: projectTimeline.project_end_date || projectInfo.endDate || '-',
        submittedDate: item.processed_date || '-',
        approvedDate: decision === 'recommend_approval' ? projectTimeline.project_end_date : null,
        awsAccountId: projectTimeline.aws_account_id || projectInfo.awsAccountId || '-',
        projectTimeline: projectTimeline.status || 'unknown'
      },
      validationStatus: getValidationStatus(validationResults.project_timeline),
      validationReason: projectTimeline.reason || '',
      sopSections: projectTimeline.sop_sections || []
    },

    // Duplicate Funding Requests Tab
    duplicateFundingRequests: {
      hasDuplicates: (detailed.duplicate_funding_requests?.duplicates?.length || 0) > 0,
      count: detailed.duplicate_funding_requests?.duplicates?.length || 0,
      reason: detailed.duplicate_funding_requests?.reason || '',
      duplicates: detailed.duplicate_funding_requests?.duplicates || [],
      validationStatus: getValidationStatus(validationResults.duplicate_funding_requests),
      validationReason: detailed.duplicate_funding_requests?.reason || ''
    },

    // Legacy fields for backward compatibility (list view)
    projectName: `${customerName} Migration`,
    requestor: partnerName,
    customer: customerName,
    amount: financial.fund_amount_requested || 0,
    submittedDate: projectTimeline.project_start_date || projectInfo.startDate || '-'
  };
};

/**
 * Fetch a single funding request by ID
 * GET /validations/{fundRequestId}
 */
export const fetchFundingRequestById = async (fundRequestId) => {
  const idToken = getIdToken();

  if (!idToken) {
    throw new Error('No authentication token found. Please log in.');
  }

  const url = `${API_CONFIG.baseUrl}/validations/${fundRequestId}`;

  const response = await fetch(url, {
    method: 'GET',
    cache: 'no-store',
    headers: {
      'Authorization': idToken,
      'Content-Type': 'application/json'
    }
  });

  if (response.status === 401) {
    throw new Error('Authentication failed. Please log in again.');
  }

  if (response.status === 403) {
    throw new Error('Access denied. You do not have permission to view this funding request.');
  }

  if (response.status === 404) {
    throw new Error(`Funding request ${fundRequestId} not found.`);
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch funding request: ${response.statusText}`);
  }

  const data = await response.json();

  // Transform nested validation_data to flat structure
  const transformed = transformValidationData(data);

  return transformed;
};

/**
 * Trigger a new validation via AgentCore
 * POST /validate
 */
export const triggerValidation = async (fundRequestId, idType = 'fundRequest') => {
  const idToken = getIdToken();

  if (!idToken) {
    throw new Error('No authentication token found. Please log in.');
  }

  const requestBody = idType === 'benefitApplication' 
    ? { benefitApplicationId: fundRequestId }
    : { fundRequestId };

  const response = await fetch(`${API_CONFIG.baseUrl}/validate`, {
    method: 'POST',
    headers: {
      'Authorization': idToken,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(requestBody)
  });

  if (response.status === 401) {
    throw new Error('Authentication failed. Please log in again.');
  }

  if (response.status === 403) {
    throw new Error('Access denied. You do not have permission to trigger validations.');
  }

  if (!response.ok) {
    try {
      const errData = await response.json();
      throw new Error(errData.error || `Failed to trigger validation: ${response.statusText}`);
    } catch (e) {
      if (e.message !== `Failed to trigger validation: ${response.statusText}`) throw e;
      throw new Error(`Failed to trigger validation: ${response.statusText}`);
    }
  }

  const data = await response.json();
  return data;
};

/**
 * Get review status for a specific funding request
 * GET /review-status/{fundRequestId}
 */
export const getReviewStatus = async (fundRequestId) => {
  const idToken = getIdToken();

  if (!idToken) {
    throw new Error('No authentication token found. Please log in.');
  }

  const response = await fetch(`${API_CONFIG.baseUrl}/review-status/${fundRequestId}`, {
    method: 'GET',
    cache: 'no-store',
    headers: {
      'Authorization': idToken,
      'Content-Type': 'application/json'
    }
  });

  if (response.status === 401) {
    throw new Error('Authentication failed. Please log in again.');
  }

  if (response.status === 403) {
    throw new Error('Access denied. You do not have permission to view review status.');
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch review status: ${response.statusText}`);
  }

  const data = await response.json();
  return data;
};

/**
 * Get calculator details for a specific funding request
 * GET /calculator-details/{fundRequestId}
 */
export const getCalculatorDetails = async (fundRequestId) => {
  const idToken = getIdToken();

  if (!idToken) {
    throw new Error('No authentication token found. Please log in.');
  }

  const response = await fetch(`${API_CONFIG.baseUrl}/calculator-details/${fundRequestId}`, {
    method: 'GET',
    cache: 'no-store',
    headers: {
      'Authorization': idToken,
      'Content-Type': 'application/json'
    }
  });

  if (response.status === 401) {
    throw new Error('Authentication failed. Please log in again.');
  }

  if (response.status === 403) {
    throw new Error('Access denied. You do not have permission to view calculator details.');
  }

  if (response.status === 404) {
    throw new Error(`Calculator details for funding request ${fundRequestId} not found. This fund request may not have pricing calculator data available.`);
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch calculator details: ${response.statusText}`);
  }

  const data = await response.json();
  return data;
};

/**
 * Process a calculator URL in standalone (ad-hoc) mode.
 * No fund request ID — results returned directly, nothing saved to DynamoDB.
 * POST /process-calculator
 */
export const processCalculatorStandalone = async (calculatorUrl) => {
  const idToken = getIdToken();

  if (!idToken) {
    throw new Error('No authentication token found. Please log in.');
  }

  const response = await fetch(`${API_CONFIG.baseUrl}/process-calculator`, {
    method: 'POST',
    headers: {
      'Authorization': idToken,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ calculator_url: calculatorUrl }),
  });

  if (response.status === 401) {
    throw new Error('Authentication failed. Please log in again.');
  }

  if (response.status === 403) {
    throw new Error('Access denied. You do not have permission to use this feature.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to process calculator (HTTP ${response.status})`);
  }

  return response.json();
};

/**
 * Transform DynamoDB data to match UI format
 * Handles both /validations (full data) and /review-status (minimal data)
 */
const transformFundingRequests = (data) => {
  console.log('Transforming funding requests data:', data);
  
  // If data is already in the correct format, return as-is
  if (Array.isArray(data)) {
    console.log('Data is already an array, returning as-is');
    return data;
  }

  // If data has 'fundRequests' property (review-status format)
  if (data.fundRequests) {
    console.log('Transforming review-status format data');
    return data.fundRequests.map(item => ({
      id: item.FR_id,
      projectName: `${item.partner_name || 'Unknown Partner'} - ${item.program || 'Unknown Program'}`,
      requestor: item.partner_name || 'Unknown Partner',
      customer: '-',
      amount: 0,
      status: item.review_state || 'pending',
      complianceScore: 0,
      reviewState: item.review_state,
      lastUpdated: item.last_updated,
      retryCount: item.retry_count,
      program: item.program
    }));
  }

  // If data has a 'validations' property (API Gateway format)
  if (data.validations) {
    console.log('Transforming validations format data, count:', data.validations.length);
    return data.validations.map(item => transformValidationData(item));
  }

  // If data is a single object, wrap in array
  if (typeof data === 'object' && data !== null) {
    console.log('Single object detected, wrapping in array');
    return [transformValidationData(data)];
  }

  console.log('No valid data structure found, returning empty array');
  return [];
};

/**
 * Submit reviewer feedback for a Chronos validation decision.
 * POST /feedback
 */
export const submitFeedback = async (fundRequestId, feedbackType, validationDecision, disagreementValidations, disagreementReason, recommendedDecision) => {
  const idToken = getIdToken();

  if (!idToken) {
    throw new Error('No authentication token found. Please log in.');
  }

  const response = await fetch(`${API_CONFIG.baseUrl}/feedback`, {
    method: 'POST',
    cache: 'no-store',
    headers: {
      'Authorization': idToken,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      fundRequestId,
      feedbackType,
      validationDecision,
      disagreementValidations: disagreementValidations || null,
      disagreementReason: disagreementReason || null,
      recommendedDecision: recommendedDecision || null,
    }),
  });

  if (response.status === 401) {
    throw new Error('Authentication failed. Please log in again.');
  }

  if (response.status === 403) {
    throw new Error('Access denied. You do not have permission to submit feedback.');
  }

  if (response.status === 409) {
    const data = await response.json();
    const error = new Error(data.error || 'Feedback already submitted for this fund request');
    error.status = 409;
    throw error;
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to submit feedback: ${response.statusText} - ${errorText}`);
  }

  return await response.json();
};

/**
 * Update existing reviewer feedback.
 * PUT /feedback
 */
export const updateFeedback = async (fundRequestId, feedbackType, validationDecision, disagreementValidations, disagreementReason, recommendedDecision) => {
  const idToken = getIdToken();

  if (!idToken) {
    throw new Error('No authentication token found. Please log in.');
  }

  const response = await fetch(`${API_CONFIG.baseUrl}/feedback`, {
    method: 'PUT',
    cache: 'no-store',
    headers: {
      'Authorization': idToken,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      fundRequestId,
      feedbackType,
      validationDecision,
      disagreementValidations: disagreementValidations || null,
      disagreementReason: disagreementReason || null,
      recommendedDecision: recommendedDecision || null,
    }),
  });

  if (response.status === 401) {
    throw new Error('Authentication failed. Please log in again.');
  }

  if (response.status === 404) {
    throw new Error('No existing feedback found to update.');
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update feedback: ${response.statusText} - ${errorText}`);
  }

  return await response.json();
};

/**
 * Retrieve existing feedback for a fund request and the authenticated reviewer.
 * GET /feedback/{fundRequestId}
 */
export const getFeedback = async (fundRequestId) => {
  const idToken = getIdToken();

  if (!idToken) {
    throw new Error('No authentication token found. Please log in.');
  }

  const response = await fetch(`${API_CONFIG.baseUrl}/feedback/${fundRequestId}`, {
    method: 'GET',
    cache: 'no-store',
    headers: {
      'Authorization': idToken,
      'Content-Type': 'application/json',
    },
  });

  if (response.status === 401) {
    throw new Error('Authentication failed. Please log in again.');
  }

  if (response.status === 403) {
    throw new Error('Access denied. You do not have permission to view feedback.');
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch feedback: ${response.statusText}`);
  }

  return await response.json();
};

/**
 * List all feedback submitted by the authenticated reviewer.
 * GET /feedback
 */
export const getAllFeedback = async () => {
  const idToken = getIdToken();

  if (!idToken) {
    throw new Error('Not authenticated. Please log in.');
  }

  const response = await fetch(`${API_CONFIG.baseUrl}/feedback`, {
    method: 'GET',
    cache: 'no-store',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (response.status === 403) {
    throw new Error('Access denied. You do not have permission to view feedback.');
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch feedback: ${response.statusText}`);
  }

  return await response.json();
};

/**
 * Fetch aggregate audit log counts from the database.
 * GET /audit-log?action=counts
 */
export const getAuditCounts = async (filters = {}) => {
  const idToken = getIdToken();
  if (!idToken) {
    throw new Error('Not authenticated. Please log in.');
  }

  const params = new URLSearchParams();
  params.set('action', 'counts');
  if (filters.startDate) params.set('start_date', filters.startDate);
  if (filters.endDate) params.set('end_date', filters.endDate);
  if (filters.tableName) params.set('table_name', filters.tableName);
  if (filters.operation) params.set('operation', filters.operation);
  if (filters.recordKey) params.set('record_key', filters.recordKey);
  if (filters.user) params.set('user', filters.user);

  const url = `${API_CONFIG.baseUrl}/audit-log?${params.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    cache: 'no-store',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch audit counts: ${response.statusText}`);
  }

  return await response.json();
};

/**
 * Fetch audit log records with optional filters.
 * GET /audit-log
 */
export const getAuditLog = async (filters = {}) => {
  const idToken = getIdToken();
  if (!idToken) {
    throw new Error('Not authenticated. Please log in.');
  }

  const params = new URLSearchParams();
  if (filters.tableName) params.set('table_name', filters.tableName);
  if (filters.operation) params.set('operation', filters.operation);
  if (filters.recordKey) params.set('record_key', filters.recordKey);
  if (filters.user) params.set('user', filters.user);
  if (filters.startDate) params.set('start_date', filters.startDate);
  if (filters.endDate) params.set('end_date', filters.endDate);
  if (filters.pageSize) params.set('page_size', filters.pageSize);
  if (filters.nextToken) params.set('next_token', filters.nextToken);

  const queryString = params.toString();
  const url = `${API_CONFIG.baseUrl}/audit-log${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    cache: 'no-store',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (response.status === 403) {
    throw new Error('Access denied. You do not have permission to view audit logs.');
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch audit log: ${response.statusText}`);
  }

  return await response.json();
};

/**
 * Log a user visit to the audit log.
 * POST /audit-log
 * Fire-and-forget — errors are silently ignored.
 */
export const logVisit = async (fundRequestId = null) => {
  try {
    const idToken = getIdToken();
    if (!idToken) return;

    await fetch(`${API_CONFIG.baseUrl}/audit-log`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: fundRequestId ? JSON.stringify({ fundRequestId }) : undefined,
    });
  } catch (e) {
    // Silent — visit logging should never block the user
    console.debug('Visit log failed:', e);
  }
};

/**
 * Submit a reply to an existing DISAGREE feedback record.
 * PATCH /feedback/{fundRequestId}/{reviewerId}/reply
 */
export const submitReply = async (fundRequestId, reviewerId, replyDisposition, replyExplanation) => {
  const idToken = getIdToken();

  if (!idToken) {
    throw new Error('No authentication token found. Please log in.');
  }

  const response = await fetch(`${API_CONFIG.baseUrl}/feedback/${fundRequestId}/${reviewerId}/reply`, {
    method: 'PATCH',
    cache: 'no-store',
    headers: {
      'Authorization': idToken,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      replyDisposition,
      replyExplanation,
    }),
  });

  if (response.status === 400) {
    const data = await response.json();
    throw new Error(data.error || 'Invalid reply data. Please check your input.');
  }

  if (response.status === 403) {
    throw new Error('Access denied. You do not have permission to reply to feedback.');
  }

  if (response.status === 404) {
    throw new Error('Feedback record not found or is not eligible for reply.');
  }

  if (response.status === 409) {
    const data = await response.json();
    const error = new Error(data.error || 'A reply already exists for this feedback record');
    error.status = 409;
    throw error;
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to submit reply: ${response.statusText} - ${errorText}`);
  }

  return await response.json();
};

/**
 * Mark an IS_AN_ISSUE feedback record as resolved.
 * PATCH /feedback/{fundRequestId}/{reviewerId}/resolve
 */
export const resolveFeedback = async (fundRequestId, reviewerId) => {
  const idToken = getIdToken();

  if (!idToken) {
    throw new Error('No authentication token found. Please log in.');
  }

  const response = await fetch(`${API_CONFIG.baseUrl}/feedback/${fundRequestId}/${reviewerId}/resolve`, {
    method: 'PATCH',
    cache: 'no-store',
    headers: {
      'Authorization': idToken,
      'Content-Type': 'application/json',
    },
  });

  if (response.status === 403) {
    throw new Error('Access denied. You do not have permission to resolve feedback.');
  }

  if (response.status === 409) {
    const data = await response.json();
    throw new Error(data.error || 'Record is already resolved or not eligible.');
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to resolve feedback: ${response.statusText} - ${errorText}`);
  }

  return await response.json();
};

export const s3Service = {
  getFundingRequests: fetchFundingRequests,
  getFundingRequestById: fetchFundingRequestById,
  triggerValidation: triggerValidation,
  getCalculatorDetails: getCalculatorDetails,
  getReviewStatus: getReviewStatus,
  getAllReviewStatus: getAllReviewStatus,
  submitFeedback: submitFeedback,
  updateFeedback: updateFeedback,
  getFeedback: getFeedback,
  getAllFeedback: getAllFeedback,
  getAuditLog: getAuditLog,
  getAuditCounts: getAuditCounts,
  logVisit: logVisit,
  submitReply: submitReply,
  resolveFeedback: resolveFeedback,
};

